import React, { useEffect } from "react";
import { SafeAreaView, StyleSheet, View, Animated, BackHandler, Alert, Platform } from "react-native";
import { NavigationContainer } from "@react-navigation/native";
import { createStackNavigator } from "@react-navigation/stack";
import colors from "./theme/colors";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NavigationVisibilityProvider, useNavigationVisibility } from "./context/NavigationVisibilityContext";

// Hide web splash screen when app loads
const hideSplashScreen = () => {
  if (Platform.OS === 'web') {
    const splashScreen = document.getElementById('splash-screen');
    if (splashScreen) {
      splashScreen.style.display = 'none';
    }
  }
};

import Header from "./components/Header";
import HomeScreen from "./screens/HomeScreen";
import LoginScreen from "./screens/LoginScreen";
import FeaturesScreen from "./screens/FeaturesScreen";
import HowItWorksScreen from "./screens/HowItWorksScreen";
import SuccessStoriesScreen from "./screens/SuccessStoriesScreen";
import ProfilesScreen from "./screens/ProfilesScreen";
import ProfileDetailScreen from "./screens/ProfileDetailScreen";
import SharedProfilesScreen from "./screens/SharedProfilesScreen";
import ShareProfileScreen from "./screens/ShareProfileScreen";
import EditEducationScreen from "./screens/EditEducationScreen";
import MemberProfileScreen from "./screens/MemberProfileScreen";
import MediatorRegistrationScreen from "./screens/MediatorRegistrationScreen";
import BottomNav from "./components/BottomNav";

const Stack = createStackNavigator();

function AppContent() {
  const [currentRoute, setCurrentRoute] = React.useState('Home');
  const { user, isAuthenticated, isLoading, setNavigationResetCallback } = useAuth();
  const { headerAnimation, bottomNavAnimation, resetVisibility } = useNavigationVisibility();
  const navigationRef = React.useRef(null);

  // Determine initial route based on user type
  const getInitialRoute = () => {
    if (isLoading) return 'Home';
    if (isAuthenticated && user?.userType === 'Mediator') {
      return 'Profiles';
    }
    if (isAuthenticated && user?.userType === 'Member') {
      return 'MyProfile';
    }
    return 'Home';
  };

  // Provide navigation reset callback to auth context
  React.useEffect(() => {
    setNavigationResetCallback(() => () => {
      if (navigationRef.current) {
        navigationRef.current.reset({
          index: 0,
          routes: [{ name: 'Home' }],
        });
      }
    });
  }, [setNavigationResetCallback]);

  // Handle navigation when auth state changes (for reloads/refreshes)
  React.useEffect(() => {
    if (!isLoading && navigationRef.current) {
      const targetRoute = getInitialRoute();
      const currentRouteName = navigationRef.current.getCurrentRoute()?.name;
      
      // Only reset navigation if we're not already on the target route
      // Use reset() instead of navigate() to prevent back navigation to previous screens
      if (currentRouteName !== targetRoute) {
        navigationRef.current.reset({
          index: 0,
          routes: [{ name: targetRoute }],
        });
      }
    }
  }, [isAuthenticated, user?.userType, isLoading]);

  // Hide web splash screen when app finishes loading
  React.useEffect(() => {
    if (!isLoading) {
      hideSplashScreen();
    }
  }, [isLoading]);

  const showNav = currentRoute !== 'ProfileDetail' && currentRoute !== 'Login';

  // Reset navigation visibility on route change
  React.useEffect(() => {
    if (resetVisibility) {
      resetVisibility();
    }
  }, [currentRoute]);

  // Handle back button (Android only)
  React.useEffect(() => {
    // Skip BackHandler on web platform
    if (Platform.OS === 'web') {
      return;
    }

    const backAction = () => {
      if (!navigationRef.current) return false;

      const state = navigationRef.current.getState();
      const currentRouteName = state?.routes[state.index]?.name;
      
      // Check if we can go back
      if (state?.routes?.length > 1) {
        navigationRef.current.goBack();
        return true;
      }
      
      // At root screen, show exit confirmation
      Alert.alert(
        'Exit App',
        'Are you sure you want to exit?',
        [
          {
            text: 'Cancel',
            onPress: () => null,
            style: 'cancel'
          },
          {
            text: 'Exit',
            onPress: () => BackHandler.exitApp()
          }
        ]
      );
      return true;
    };

    const backHandler = BackHandler.addEventListener('hardwareBackPress', backAction);

    return () => backHandler.remove();
  }, []);

  return (
    <NavigationContainer
      ref={navigationRef}
      onStateChange={(state) => {
        const route = state?.routes[state.index];
        if (route) {
          setCurrentRoute(route.name);
        }
      }}
    >
      <SafeAreaView style={styles.safe}>
        {showNav && (
          <Animated.View style={[styles.statusBarSpace, { 
            transform: [{ translateY: headerAnimation }] 
          }]} />
        )}
        {showNav && <Header />}
        
        <Animated.View style={[
          styles.content, 
          showNav && { transform: [{ translateY: headerAnimation }] },
          !showNav && styles.fullScreenContent
        ]}>
          <Stack.Navigator
            screenOptions={{ headerShown: false }}
            initialRouteName={getInitialRoute()}
          >
            <Stack.Screen name="Home" component={HomeScreen} />
            <Stack.Screen name="Login" component={LoginScreen} />
            <Stack.Screen name="Features" component={FeaturesScreen} />
            <Stack.Screen name="HowItWorks" component={HowItWorksScreen} />
            <Stack.Screen name="SuccessStories" component={SuccessStoriesScreen} />
            <Stack.Screen name="Profiles" component={ProfilesScreen} />
            <Stack.Screen name="ProfileDetail" component={ProfileDetailScreen} />
            <Stack.Screen name="MyProfile" component={MemberProfileScreen} />
            <Stack.Screen name="SharedProfiles" component={SharedProfilesScreen} />
            <Stack.Screen name="ShareProfile" component={ShareProfileScreen} />
            <Stack.Screen name="EditEducation" component={EditEducationScreen} />
            <Stack.Screen name="MemberProfile" component={MemberProfileScreen} />
            <Stack.Screen name="AddProfile" component={require('./screens/AddProfileScreen').default} />
            <Stack.Screen name="MediatorRegistration" component={MediatorRegistrationScreen} />
            <Stack.Screen name="ChangePassword" component={require('./screens/ChangePasswordScreen').default} />
          </Stack.Navigator>
        </Animated.View>

        {showNav && <BottomNav />}
      </SafeAreaView>
    </NavigationContainer>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <NavigationVisibilityProvider>
        <AppContent />
      </NavigationVisibilityProvider>
    </AuthProvider>
  );
}

const styles = StyleSheet.create({
  safe: {
    flex: 1,
    backgroundColor: colors.background,
  },
  statusBarSpace: {
    height: 24,
    backgroundColor: colors.primary,
  },
  content: {
    flex: 1,
    paddingTop: 60,
    paddingBottom: 70,
  },
  fullScreenContent: {
    paddingTop: 0,
    paddingBottom: 0,
  },
});
